import React, { useContext, useEffect, useState } from "react";
import { Box, Fade, Switch, Typography } from "@mui/material";
import SchoolSwitcherList from "./SchoolSwitcherList";
import SchoolNavMenu from "./SchoolNavMenu";
import RosterActions from "./RosterActions";
import { NavDrawerContext } from "../shared/NavDrawerButton";
import { useFixedSidebar } from "../../contexts/FixedSidebarContext";

// The School app menu, shared by the desktop hamburger drawer (sidebar toggle
// off) and the permanent left sidebar (toggle on). It owns its own flex/padding
// so it drops into either shell unchanged.
export default function SchoolNavContents({
  appProps,
  schoolSlug,
  schoolYearId = null,
  navigationMode,
}) {
  const { enabled, setEnabled } = useFixedSidebar();
  const { open: drawerOpen } = useContext(NavDrawerContext);
  const [switcherOpen, setSwitcherOpen] = useState(false);

  // Collapse the switcher back to the menu whenever the surrounding drawer closes,
  // so reopening it always lands on the menu. Outside a drawer (the permanent
  // sidebar) the context defaults to open, so this never fires there.
  useEffect(() => {
    if (!drawerOpen) setSwitcherOpen(false);
  }, [drawerOpen]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Scrollable region: only the menu scrolls when it overflows, so the
          fixed-sidebar switch below stays pinned and always visible. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 2,
        }}
      >
        {/* Switch school: collapsed trigger that, when opened, fades the menu
            below out and reveals an inline searchable school list in its place. */}
        <SchoolSwitcherList
          staff={appProps?.staff}
          currentSchoolSlug={schoolSlug}
          currentSchoolName={appProps?.school?.name}
          open={switcherOpen}
          onToggle={() => setSwitcherOpen((o) => !o)}
        />

        {/* The menu fades out and leaves layout while the switcher is open, so the
            inline school list takes its place. Closing is instant (enter: 0) so the
            menu does not briefly render below the still-present panel and jump up
            when it unmounts. */}
        <Fade
          in={!switcherOpen}
          unmountOnExit
          timeout={{ enter: 0, exit: 150 }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* OneRoster import/export + DataRollup sync (gated via appProps) */}
            <RosterActions appProps={appProps} schoolSlug={schoolSlug} />

            {/* Full navigation tree, gated like the dashboard (inline accordion). */}
            <SchoolNavMenu
              appProps={appProps}
              schoolSlug={schoolSlug}
              schoolYearId={schoolYearId}
              navigationMode={navigationMode}
            />
          </Box>
        </Fade>
      </Box>

      {/* Fixed-sidebar toggle, pinned below the scroll region. Hidden on mobile
          where there is no permanent sidebar to lock to. */}
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.5,
          borderTop: "1px solid #00000014",
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontSize: 14, color: "#00000099" }}>
          Locked to left screen
        </Typography>
        <Switch
          size="small"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          slotProps={{ input: { "aria-label": "Locked to left screen" } }}
        />
      </Box>
    </Box>
  );
}
